// Here is the updated component. I have refactored the structure to include the **Tabs system** you requested.

// ### Key Changes:

// 1.  **Tabs Implementation**: Added a `Tabs` component with two options: "Volunteer" and "Donate".
// 2.  **Button-like Styling**: I customized the MUI `sx` prop on the Tabs to remove the standard underline indicator and give the tabs a solid, rounded button look using your accent color `#c18078`.
// 3.  **Volunteer Form**: Adapted from your `StaffCreateForm` but simplified for a volunteer context (you can adjust the specific fields as needed).
// 4.  **Donate Component**: Added a placeholder component for where your Donate logic will sit.

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Tab,
  Tabs,
  Paper
} from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import useApi from '../../api/useApi';
import { useLocation, useNavigate } from 'react-router';
// Make sure to import your actual API service for volunteers here
import { VolunteerCreate  } from '../../api/orgService'; 
import DonateButton from '../sections/DonateButton'
// --- Placeholder for your Donate Component ---
const DonateComponent = () => {
    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            p={5} 
            border="1px dashed #ccc"
            borderRadius={2}
            bgcolor="#fafafa"
        >
            <Typography variant="h6" color="#c18078">Support Our Cause</Typography>
            <Typography variant="body1" paragraph>
                Your donate component content goes here.
            </Typography>
            {/* <Button variant="contained" sx={{ bgcolor: '#c18078', '&:hover': { bgcolor: '#a66a63' } }}> */}
                {/* Donate Now */}
                <DonateButton />
            {/* </Button> */}
            
        </Box>
    );
};

const VolunteerPage = () => {

    const location = useLocation();
    const navigate = useNavigate();
  
    // 1. Determine initial tab based on the current URL hash
    // If hash is '#donate', set to 1, otherwise default to 0 (volunteer)
    const getInitialTab = () => {
      return location.hash === '#donate' ? 1 : 0;
    };
  
    const [tabValue, setTabValue] = useState(getInitialTab());
  
    // 2. Sync Tab -> URL
    // When the user clicks a tab, update the URL hash
    const handleTabChange = (event, newValue) => {
      setTabValue(newValue);
      if (newValue === 1) {
          navigate('#donate'); // or window.location.hash = 'donate';
      } else {
          navigate('#volunteer');
      }
    };
  
    // 3. Sync URL -> Tab (Optional but recommended)
    // This ensures the back button works (e.g. if user hits Back from Donate -> Volunteer)
    useEffect(() => {
      if (location.hash === '#donate') {
        setTabValue(1);
      } else {
        setTabValue(0);
      }
    }, [location.hash])

  const { user } = useAuth();
  
  // Color Accent
  const PRIMARY_COLOR = '#c18078';

  // --- Form State ---
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    motivation: '',
  });

  // --- API Hooks (Placeholder logic based on your staff form) ---
  // Replace 'VolunteerCreate' with your actual API function
  const { 
    request: createVolunteer, 
    data: createData, 
    error: createError, 
    isLoading 
  } = useApi(VolunteerCreate); 

//   const handleTabChange = (event, newValue) => {
//     setTabValue(newValue);
//   };

  const handleChange = (e) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // --- Effects for Toast Notifications ---
  useEffect(() => {
    if (createData?.status === 201) {
      toast.success("Volunteer application submitted successfully!", { position: "top-center" });
      // Reset form or redirect
      setForm({ email: '', first_name: '', last_name: '', phone: '', motivation: '' });
    }
  }, [createData]);

  useEffect(() => {
    if (createError) {
      toast.error(`${JSON.stringify(createError?.response?.data || "Error occurred")}`, { position: "top-center" });
    }
  }, [createError]);

  // --- Validation Logic ---
  const okayToSubmit = !form.first_name || !form.last_name || !form.email || isLoading;

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSend = {
      ...form,
      created_by: user?.user?.id
    };
    createVolunteer(dataToSend);
  };

  return (
    <Box display="flex" justifyContent="center" width="100%">
      <Box display="flex" flexDirection="column" mt={4} width={700}>
        
        {/* Header */}
        <Box mb={3}>
          <Typography variant="h5" gutterBottom>
            Get Involved
          </Typography>
          <Typography variant='body1' color='#404040'>
            Join us as a volunteer or support our mission through a donation.
          </Typography>
        </Box>

        {/* Custom Button-like Tabs */}
        <Box mb={4}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="volunteer and donate tabs"
            // Hide the default underline indicator
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              '& .MuiTabs-flexContainer': {
                gap: 2 // Space between buttons
              }
            }}
          >
            <Tab 
              label="Join as Volunteer" 
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '50px', // Pill shape
                border: `1px solid ${PRIMARY_COLOR}`,
                color: PRIMARY_COLOR,
                padding: '10px 24px',
                '&.Mui-selected': {
                  backgroundColor: PRIMARY_COLOR,
                  color: '#fff',
                },
                '&:hover': {
                    backgroundColor: 'rgba(193, 128, 120, 0.1)',
                }
              }} 
            />
            <Tab 
              label="Make a Donation" 
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '50px',
                border: `1px solid ${PRIMARY_COLOR}`,
                color: PRIMARY_COLOR,
                padding: '10px 24px',
                '&.Mui-selected': {
                  backgroundColor: PRIMARY_COLOR,
                  color: '#fff',
                },
                '&:hover': {
                    backgroundColor: 'rgba(193, 128, 120, 0.1)',
                }
              }} 
            />
          </Tabs>
        </Box>

        {/* --- TAB 1: VOLUNTEER FORM --- */}
        {tabValue === 0 && (
          <Box component={Paper} elevation={0} >
            <form onSubmit={handleSubmit}>
              <Typography variant="subtitle2" gutterBottom>
                <i>Required fields are marked with an asterisk (*).</i>
              </Typography>

              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
              </Box>

              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                margin="normal"
              />

              <TextField
                fullWidth
                label="Why do you want to volunteer?"
                name="motivation"
                value={form.motivation}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={4}
                placeholder="Tell us a bit about yourself and how you'd like to help..."
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ 
                    mt: 3, 
                    mb: 5,
                    bgcolor: PRIMARY_COLOR,
                    '&:hover': {
                        bgcolor: '#a66a63' // Slightly darker shade for hover
                    }
                }}
                disabled={okayToSubmit}
              >
                Submit Application
              </Button>
            </form>
          </Box>
        )}

        {/* --- TAB 2: DONATE COMPONENT --- */}
        {tabValue === 1 && (
          <Box>
             <DonateComponent />
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default VolunteerPage;